import {
    AlertTriangle,
    BadgeCheck,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Info,
    LoaderCircle,
    Plus,
    Sparkles,
    ToggleLeft,
    ToggleRight,
    Zap,
} from 'lucide-react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './AddComponentScreen.module.css'

// ── Static data ───────────────────────────────────────────────────────────────
const COMPONENT_OPTIONS = [
    { code: 'BASIC',            label: 'Basic Pay',               category: 'EARNING',   isStatutory: false },
    { code: 'HRA',              label: 'House Rent Allowance',    category: 'EARNING',   isStatutory: false },
    { code: 'TRAVEL_ALLOWANCE', label: 'Travel Allowance',        category: 'EARNING',   isStatutory: false },
    { code: 'SPECIAL_ALLOWANCE',label: 'Special Allowance',       category: 'EARNING',   isStatutory: false },
    { code: 'ATTENDANCE_BONUS', label: 'Attendance Bonus',        category: 'EARNING',   isStatutory: false },
    { code: 'CUSTOM_ALLOWANCE', label: 'Custom Allowance',        category: 'EARNING',   isStatutory: false },
    { code: 'LWP_DEDUCTION',    label: 'Leave Without Pay',       category: 'DEDUCTION', isStatutory: true  },
    { code: 'LATE_DEDUCTION',   label: 'Late Arrival Deduction',  category: 'DEDUCTION', isStatutory: true  },
    { code: 'PF_EMPLOYEE',      label: 'Provident Fund (PF)',     category: 'DEDUCTION', isStatutory: true  },
    { code: 'ESI_EMPLOYEE',     label: 'ESI',                     category: 'DEDUCTION', isStatutory: true  },
    { code: 'PROFESSIONAL_TAX', label: 'Professional Tax',        category: 'DEDUCTION', isStatutory: true  },
    { code: 'TDS',              label: 'TDS',                     category: 'DEDUCTION', isStatutory: true  },
    { code: 'ADVANCE_EMI',      label: 'Advance Recovery (EMI)',  category: 'DEDUCTION', isStatutory: true  },
    { code: 'CUSTOM_DEDUCTION', label: 'Custom Deduction',        category: 'DEDUCTION', isStatutory: false },
]

const SUGGESTED_NAMES = {
    BASIC:            'Basic Pay',
    HRA:              'House Rent Allowance',
    TRAVEL_ALLOWANCE: 'Travel Allowance',
    SPECIAL_ALLOWANCE:'Special Allowance',
    ATTENDANCE_BONUS: 'Attendance Bonus',
    LWP_DEDUCTION:    'Leave Without Pay Deduction',
    LATE_DEDUCTION:   'Late Arrival Deduction',
    PF_EMPLOYEE:      'Provident Fund',
    ESI_EMPLOYEE:     'Employee State Insurance',
    PROFESSIONAL_TAX: 'Professional Tax',
    TDS:              'Tax Deducted at Source',
    ADVANCE_EMI:      'Salary Advance Recovery',
    CUSTOM_ALLOWANCE: '',
    CUSTOM_DEDUCTION: '',
}

const STATUTORY_DESC = {
    LWP_DEDUCTION:    'Deducted proportionally for absent days. Based on working days in the pay cycle.',
    LATE_DEDUCTION:   'Applied when staff exceeds the grace period for late arrivals. Configured in Payroll Cycle settings.',
    PF_EMPLOYEE:      '12% of Basic Pay (employer matches), capped on ₹15,000 Basic. Computed per compliance rules.',
    ESI_EMPLOYEE:     '0.75% of Gross Salary. Applicable when gross ≤ ₹21,000/month.',
    PROFESSIONAL_TAX: 'State-mandated slab-based deduction. Configured in Compliance Settings.',
    TDS:              'Tax Deducted at Source on projected annual income, computed per IT slabs.',
    ADVANCE_EMI:      'Monthly EMI recovery against salary advances. Managed via Staff Advance ledger.',
}

const VALUE_TYPE_OPTIONS = [
    { value: 'FLAT',             label: 'Fixed Amount (₹)', hint: 'e.g. ₹14,000 flat every month' },
    { value: 'PERCENT_OF_BASIC', label: '% of Basic Pay',   hint: 'e.g. 40% of Basic Pay' },
    { value: 'PERCENT_OF_GROSS', label: '% of Total Gross', hint: 'e.g. 5% of Gross Salary' },
]

const parseActiveStore = (raw) => {
    try { return raw && raw.trim() !== '' ? JSON.parse(raw) : null } catch { return null }
}

// ── Component ─────────────────────────────────────────────────────────────────
const AddComponentScreen = () => {
    const navigate  = useNavigate()
    const location  = useLocation()
    const { templateIdentifier } = useParams()
    const authCtx   = useContext(AuthContext)
    const asCtx     = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    // prefilledCode can come from location.state (SalaryStructureDetailScreen empty-state CTA)
    const prefilledCode    = location.state?.prefilledCode || ''
    const routeTemplateName = location.state?.templateName || 'Template'

    // ── Form state ───────────────────────────────────────────────
    const [formData, setFormData] = useState({
        componentName: prefilledCode ? (SUGGESTED_NAMES[prefilledCode] ?? '') : '',
        componentCode: prefilledCode || '',
        valueType:     '',
        value:         '',
        isTaxable:     true,
        sortOrder:     '',
    })
    const [errors,   setErrors]   = useState({})
    const [isSaving, setIsSaving] = useState(false)

    // Code picker dropdown
    const [pickerOpen, setPickerOpen] = useState(false)
    const pickerRef = useRef(null)

    // Value type dropdown
    const [vtPickerOpen, setVtPickerOpen] = useState(false)
    const vtPickerRef = useRef(null)

    // ── Derived ──────────────────────────────────────────────────
    const selectedOption  = COMPONENT_OPTIONS.find((o) => o.code === formData.componentCode)
    const isStatutoryCode = selectedOption?.isStatutory ?? false
    const earnings        = COMPONENT_OPTIONS.filter((o) => o.category === 'EARNING')
    const deductions      = COMPONENT_OPTIONS.filter((o) => o.category === 'DEDUCTION')
    const selectedVT      = VALUE_TYPE_OPTIONS.find((o) => o.value === formData.valueType)

    const valueLabelMap = {
        FLAT:             'Amount (₹) *',
        PERCENT_OF_BASIC: 'Percentage (%) *',
        PERCENT_OF_GROSS: 'Percentage (%) *',
    }
    const valueLabel = formData.valueType ? valueLabelMap[formData.valueType] : 'Value *'

    // ── Close dropdowns on outside click ─────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        const handler = (e) => {
            if (vtPickerRef.current && !vtPickerRef.current.contains(e.target)) setVtPickerOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    // ── Handlers ─────────────────────────────────────────────────
    const handleCodeSelect = (code) => {
        const option = COMPONENT_OPTIONS.find((o) => o.code === code)
        const suggestedName = SUGGESTED_NAMES[code] ?? ''
        setFormData((prev) => ({
            ...prev,
            componentCode: code,
            componentName: suggestedName,
            valueType:     option?.isStatutory ? 'STATUTORY' : '',
            value:         '',
            isTaxable:     !(code === 'HRA' || code === 'TRAVEL_ALLOWANCE'),
        }))
        setErrors({})
        setPickerOpen(false)
    }

    const handleVTSelect = (vt) => {
        setFormData((prev) => ({ ...prev, valueType: vt, value: '' }))
        setErrors((prev) => ({ ...prev, valueType: undefined }))
        setVtPickerOpen(false)
    }

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    // ── Validation ───────────────────────────────────────────────
    const validate = () => {
        const newErrors = {}
        if (!formData.componentCode) newErrors.componentCode = 'Please select a component type.'
        if (!formData.componentName.trim()) newErrors.componentName = 'Component name is required.'
        if (!isStatutoryCode) {
            if (!formData.valueType) newErrors.valueType = 'Please select how this component is calculated.'
            if (formData.value === '' || formData.value === null) {
                newErrors.value = 'Value is required.'
            } else if (parseFloat(formData.value) < 0) {
                newErrors.value = 'Value must be 0 or greater.'
            } else if (
                ['PERCENT_OF_BASIC', 'PERCENT_OF_GROSS'].includes(formData.valueType) &&
                parseFloat(formData.value) > 100
            ) {
                newErrors.value = 'Percentage must be between 0 and 100.'
            }
        }
        return newErrors
    }

    // ── API call ─────────────────────────────────────────────────
    const addComponent = async () => {
        const newErrors = validate()
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})

        if (!authCtx?.token || !activeStore?.storeIdentifier || !templateIdentifier) return

        try {
            setIsSaving(true)

            const payload = {
                token:              authCtx.token,
                storeIdentifier:    activeStore.storeIdentifier,
                templateIdentifier,
                componentName:      formData.componentName.trim(),
                componentCode:      formData.componentCode,
            }

            if (!isStatutoryCode) {
                payload.valueType = formData.valueType
                payload.value     = parseFloat(formData.value) || 0
                payload.isTaxable = formData.isTaxable ? 1 : 0
            }

            if (formData.sortOrder) {
                payload.sortOrder = parseInt(formData.sortOrder, 10)
            }

            const response = await fetch(BASE_URL_API + 'addSalaryStructureComponent', {
                method:  'POST',
                body:    JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success(`"${formData.componentName.trim()}" added successfully.`)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') { authCtx.logout(); return }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (respo.message?.includes('componentName')) {
                        setErrors((prev) => ({ ...prev, componentName: respo.message }))
                    } else {
                        toast.error(respo.message)
                    }
                    break
                case 'VAL_002':
                    setErrors((prev) => ({ ...prev, value: respo.message }))
                    break
                case 'VAL_003':
                    if (respo.message?.includes('valueType')) {
                        setErrors((prev) => ({ ...prev, valueType: respo.message }))
                    } else {
                        setErrors((prev) => ({ ...prev, componentCode: respo.message }))
                    }
                    break
                case 'COMP_001':
                    setErrors((prev) => ({
                        ...prev,
                        componentCode: 'BASIC already exists in this template. Use "Revise Component" to update the amount.',
                    }))
                    break
                case 'COMP_002':
                    toast.error('Add a BASIC component first before adding percentage-based components.')
                    break
                case 'COMP_007':
                    setErrors((prev) => ({
                        ...prev,
                        componentCode: `${formData.componentCode} already exists in this template.`,
                    }))
                    break
                case 'COMP_004':
                case 'VAL_008':
                    toast.error(respo.message)
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Failed to add component.')
            }
        } catch (error) {
            console.error('addSalaryStructureComponent error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    // ─────────────────────────────────────────────────────────────
    return (
        <div className={styles.page}>

            {/* ── Header ── */}
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Add Component</span>
                    <h1 className={styles.headerTitle}>{routeTemplateName}</h1>
                </div>
            </header>

            <div className={styles.formWrap}>

                {/* ── Intro card ── */}
                <div className={styles.introCard}>
                    <Zap size={16} className={styles.introIcon} />
                    <p>
                        Components define how each staff member's salary is built.
                        Start with <strong>Basic Pay</strong> — all percentage-based
                        components anchor to it.
                    </p>
                </div>

                {/* ════════ Component type picker ════════ */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                        Component Type <span className={styles.required}>*</span>
                    </label>

                    <div className={styles.pickerWrap} ref={pickerRef}>
                        <button
                            type="button"
                            className={`${styles.pickerTrigger} ${errors.componentCode ? styles.pickerTriggerError : ''} ${pickerOpen ? styles.pickerTriggerOpen : ''}`}
                            onClick={() => setPickerOpen((p) => !p)}
                        >
                            {selectedOption ? (
                                <span className={styles.pickerSelectedLabel}>
                                    <span className={`${styles.categoryDot} ${selectedOption.category === 'EARNING' ? styles.earningDot : styles.deductionDot}`} />
                                    {selectedOption.label}
                                    {selectedOption.isStatutory && <span className={styles.autoBadge}><BadgeCheck size={10} /> Auto</span>}
                                </span>
                            ) : (
                                <span className={styles.pickerPlaceholder}>Select a component…</span>
                            )}
                            <ChevronDown size={15} className={`${styles.chevron} ${pickerOpen ? styles.chevronOpen : ''}`} />
                        </button>

                        {pickerOpen && (
                            <div className={styles.pickerDropdown}>
                                {/* Earnings group */}
                                <p className={styles.pickerGroupLabel}>
                                    <span className={styles.groupDot} style={{ background: '#16a34a' }} />
                                    EARNINGS
                                </p>
                                {earnings.map((opt) => (
                                    <button
                                        key={opt.code}
                                        type="button"
                                        className={`${styles.pickerOption} ${formData.componentCode === opt.code ? styles.pickerOptionActive : ''}`}
                                        onClick={() => handleCodeSelect(opt.code)}
                                    >
                                        <span className={`${styles.categoryDot} ${styles.earningDot}`} />
                                        <span className={styles.pickerOptionLabel}>{opt.label}</span>
                                        {formData.componentCode === opt.code && <ChevronRight size={13} className={styles.checkMark} />}
                                    </button>
                                ))}

                                {/* Deductions group */}
                                <p className={`${styles.pickerGroupLabel} ${styles.pickerGroupLabelGap}`}>
                                    <span className={styles.groupDot} style={{ background: '#dc2626' }} />
                                    DEDUCTIONS
                                </p>
                                {deductions.map((opt) => (
                                    <button
                                        key={opt.code}
                                        type="button"
                                        className={`${styles.pickerOption} ${formData.componentCode === opt.code ? styles.pickerOptionActive : ''}`}
                                        onClick={() => handleCodeSelect(opt.code)}
                                    >
                                        <span className={`${styles.categoryDot} ${styles.deductionDot}`} />
                                        <span className={styles.pickerOptionLabel}>{opt.label}</span>
                                        {opt.isStatutory && <span className={styles.autoBadgeSmall}>Auto</span>}
                                        {formData.componentCode === opt.code && <ChevronRight size={13} className={styles.checkMark} />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {errors.componentCode && (
                        <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.componentCode}</p>
                    )}
                </div>

                {/* ════════ Display name ════════ */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                        Display Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        className={`${styles.textInput} ${errors.componentName ? styles.textInputError : ''}`}
                        type="text"
                        placeholder="e.g. House Rent Allowance"
                        maxLength={200}
                        value={formData.componentName}
                        onChange={(e) => handleChange('componentName', e.target.value)}
                    />
                    {formData.componentName.length > 150 && (
                        <p className={styles.charCount}>{formData.componentName.length}/200</p>
                    )}
                    {errors.componentName && (
                        <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.componentName}</p>
                    )}
                </div>

                {/* ════════ STATUTORY info block ════════ */}
                {isStatutoryCode && formData.componentCode && (
                    <div className={styles.statutoryCard}>
                        <div className={styles.statutoryHeader}>
                            <BadgeCheck size={16} className={styles.statutoryIcon} />
                            <span>Statutory Component — Auto-computed</span>
                        </div>
                        <p className={styles.statutoryDesc}>
                            {STATUTORY_DESC[formData.componentCode] ||
                                'This component is computed automatically by the payroll engine based on your compliance settings. No manual value is needed.'}
                        </p>
                        <div className={styles.statutoryNote}>
                            <Info size={12} />
                            Compliance rules are configured in <strong>Payroll Setup → Statutory Compliance</strong>.
                        </div>
                    </div>
                )}

                {/* ════════ NON-STATUTORY fields ════════ */}
                {!isStatutoryCode && formData.componentCode && (
                    <>
                        {/* Calculation method */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                                How is this calculated? <span className={styles.required}>*</span>
                            </label>

                            <div className={styles.pickerWrap} ref={vtPickerRef}>
                                <button
                                    type="button"
                                    className={`${styles.pickerTrigger} ${errors.valueType ? styles.pickerTriggerError : ''} ${vtPickerOpen ? styles.pickerTriggerOpen : ''}`}
                                    onClick={() => setVtPickerOpen((p) => !p)}
                                >
                                    {selectedVT ? (
                                        <span className={styles.vtSelectedLabel}>
                                            {selectedVT.label}
                                            <span className={styles.vtHint}>{selectedVT.hint}</span>
                                        </span>
                                    ) : (
                                        <span className={styles.pickerPlaceholder}>Select calculation method…</span>
                                    )}
                                    <ChevronDown size={15} className={`${styles.chevron} ${vtPickerOpen ? styles.chevronOpen : ''}`} />
                                </button>

                                {vtPickerOpen && (
                                    <div className={styles.pickerDropdown}>
                                        {VALUE_TYPE_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`${styles.pickerOption} ${formData.valueType === opt.value ? styles.pickerOptionActive : ''}`}
                                                onClick={() => handleVTSelect(opt.value)}
                                            >
                                                <span className={styles.vtOptionLabel}>{opt.label}</span>
                                                <span className={styles.vtOptionHint}>{opt.hint}</span>
                                                {formData.valueType === opt.value && <ChevronRight size={13} className={styles.checkMark} />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {errors.valueType && (
                                <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.valueType}</p>
                            )}
                        </div>

                        {/* Value */}
                        {formData.valueType && (
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>{valueLabel}</label>
                                <div className={styles.valueInputWrap}>
                                    <span className={styles.valuePrefix}>
                                        {formData.valueType === 'FLAT' ? '₹' : '%'}
                                    </span>
                                    <input
                                        className={`${styles.valueInput} ${errors.value ? styles.textInputError : ''}`}
                                        type="number"
                                        inputMode="decimal"
                                        placeholder={formData.valueType === 'FLAT' ? '14000' : '40'}
                                        min="0"
                                        max={['PERCENT_OF_BASIC', 'PERCENT_OF_GROSS'].includes(formData.valueType) ? '100' : undefined}
                                        value={formData.value}
                                        onChange={(e) => handleChange('value', e.target.value)}
                                    />
                                </div>
                                {errors.value && (
                                    <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.value}</p>
                                )}
                            </div>
                        )}

                        {/* Taxable toggle */}
                        <div className={styles.toggleRow}>
                            <div className={styles.toggleMeta}>
                                <p className={styles.toggleLabel}>Taxable Earning</p>
                                <p className={styles.toggleHint}>
                                    {formData.isTaxable
                                        ? 'Included in gross for TDS computation.'
                                        : 'Tax-exempt (e.g. HRA under Section 10(13A)).'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.toggleBtn}
                                onClick={() => handleChange('isTaxable', !formData.isTaxable)}
                                aria-pressed={formData.isTaxable}
                            >
                                {formData.isTaxable
                                    ? <ToggleRight size={32} className={styles.toggleOn} />
                                    : <ToggleLeft  size={32} className={styles.toggleOff} />}
                            </button>
                        </div>

                        {/* Sort order — advanced, optional */}
                        <details className={styles.advancedDetails}>
                            <summary className={styles.advancedSummary}>
                                Advanced options
                            </summary>
                            <div className={styles.fieldGroup} style={{ marginTop: '12px' }}>
                                <label className={styles.fieldLabel}>
                                    Sort Order
                                    <span className={styles.fieldOptional}> (optional)</span>
                                </label>
                                <input
                                    className={styles.textInput}
                                    type="number"
                                    inputMode="numeric"
                                    placeholder="e.g. 55"
                                    min="1"
                                    value={formData.sortOrder}
                                    onChange={(e) => handleChange('sortOrder', e.target.value)}
                                />
                                <p className={styles.fieldHint}>
                                    Lower numbers appear first on payslips. Leave blank to use default ordering.
                                </p>
                            </div>
                        </details>
                    </>
                )}

                {/* ════════ Save button ════════ */}
                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving}
                    onClick={addComponent}
                >
                    {isSaving ? (
                        <><LoaderCircle size={16} className={styles.spin} /> Adding…</>
                    ) : (
                        <><Plus size={16} /> Add Component</>
                    )}
                </button>

            </div>
        </div>
    )
}

export default AddComponentScreen